import { useEffect, useMemo, useId, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { resolveTradingViewFuturesSymbol } from "@/lib/futures-symbols";

declare global {
  interface Window {
    TradingView?: {
      widget: new (options: Record<string, unknown>) => TVWidget;
    };
  }
}

interface TVWidget {
  onChartReady?: (cb: () => void) => void;
  activeChart?: () => TVChart | undefined;
  remove?: () => void;
}

interface TVChart {
  /** Charting Library API — may be undefined on the embedded tv.js
   *  build. Always wrap calls in try/catch. */
  createShape?: (
    point: { time?: number; price?: number; channel?: string },
    options: Record<string, unknown>,
  ) => string | number | undefined;
  /** Adds an indicator at runtime. The 5th argument carries plot
   *  overrides (lowercase keys per TV's
   *  MovingAverageExponentialIndicatorOverrides interface, e.g.
   *  `plot.color`). This is the ONLY path that honors per-instance
   *  colors on the free embed — the constructor `studies` array
   *  silently ignores `overrides`. */
  createStudy?: (
    name: string,
    forceOverlay?: boolean,
    lock?: boolean,
    inputs?: unknown,
    overrides?: Record<string, unknown>,
  ) => unknown;
  removeEntity?: (id: string | number) => void;
  removeAllShapes?: () => void;
}

/**
 * Position to overlay on the chart. Subset of `PositionLive`; passing
 * the smallest shape we need so the chart component stays decoupled
 * from the dashboard store.
 */
export interface ChartPosition {
  ticket: number;
  symbol: string;
  side: "buy" | "sell";
  lots: number;
  entry: number;
  /** Best-effort timestamp for the entry. We don't persist a true
   *  `openedAt` end-to-end yet — the bot only emits `updatedAt` on
   *  every position frame — so the marker may sit at the latest tick
   *  rather than the actual open candle. The horizontal price line is
   *  what's load-bearing for "where did I open"; the arrow is a hint. */
  updatedAt: number;
}

interface TradingViewFuturesChartProps {
  symbol: string;
  market?: "futures" | "fx-cfd";
  /** Live open positions on the active pair. Only positions whose
   *  `symbol` (alphanumeric stem) matches the active chart symbol get
   *  drawn — cross-symbol positions surface in the SiblingPositionsStrip
   *  instead. */
  positions?: ChartPosition[];
}

export function TradingViewFuturesChart({
  symbol,
  market = "futures",
  positions = [],
}: TradingViewFuturesChartProps): ReactNode {
  const id = useId();
  const containerId = useMemo(
    () => `tv-${market}-${id.replace(/[^a-zA-Z0-9_-]/g, "")}`,
    [id, market],
  );
  const widgetRef = useRef<TVWidget | null>(null);
  const chartReadyRef = useRef<boolean>(false);
  const shapeIdsRef = useRef<Map<number, Array<string | number>>>(new Map());
  const [chartApiSupported, setChartApiSupported] = useState<boolean | null>(null);
  const tvSymbol =
    market === "futures"
      ? resolveTradingViewFuturesSymbol(symbol)
      : resolveTradingViewFxCfdSymbol(symbol);

  const symbolMatchedPositions = useMemo<ChartPosition[]>(() => {
    const target = symbol.toUpperCase().replace(/[^A-Z0-9]/g, "");
    return positions.filter((p) => {
      const stem = p.symbol.toUpperCase().replace(/[^A-Z0-9]/g, "");
      return stem === target || stem.startsWith(target);
    });
  }, [positions, symbol]);

  useEffect(() => {
    let cancelled = false;
    chartReadyRef.current = false;
    shapeIdsRef.current = new Map();
    setChartApiSupported(null);

    const mount = () => {
      if (cancelled || !window.TradingView) return;
      widgetRef.current?.remove?.();
      const widget = new window.TradingView.widget({
        autosize: true,
        symbol: tvSymbol,
        interval: "5",
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        container_id: containerId,
        hide_side_toolbar: false,
        allow_symbol_change: true,
        save_image: false,
        calendar: false,
        // tv.js auto-injects "Volume@tv-basicstudies" unless we disable
        // it here. Operator wants a clean candle chart with the MA
        // overlays only — Volume on FX/CFD pairs is broker-tick-volume
        // noise anyway.
        disabled_features: ["create_volume_indicator_by_default"],
        support_host: "https://www.tradingview.com",
        // The free tv.js embed does NOT expose `createStudy` or any
        // per-instance overrides — the Advanced Charting Library
        // methods (`activeChart()`, `createStudy(..., overrides)`)
        // simply don't exist on this iframe build. The only working
        // path is the constructor `studies` array + a global
        // `studies_overrides` map, and each override key is scoped
        // PER STUDY TYPE — so two EMA instances would collapse to a
        // single color. Workaround: use three DIFFERENT MA families
        // so each gets its own override namespace.
        //   EMA 100 (Moving Average Exponential) → light purple
        //   WMA 200 (Moving Average Weighted)    → bright red
        //                                          (visually within
        //                                          1–2 candles of an
        //                                          EMA 200 as a slow
        //                                          trend filter)
        //   SMA 200 (Moving Average)             → bright cyan
        //
        // PLOT NAME GOTCHA: TradingView's marketing docs say the
        // override key for line color is `"<study>.plot.color"`. For
        // the MA family that string is silently dropped — the actual
        // plot name in the Style dialog is "MA", so the working key
        // is `"<study>.ma.color"`. Same for linewidth. Verified
        // against `s3.tradingview.com/tv.js`.
        //
        // LENGTH GOTCHA: passing length via studies_overrides is
        // also unreliable on the free embed — push it through the
        // per-study `inputs` object instead.
        studies: [
          { id: "MAExp@tv-basicstudies", inputs: { length: 100 } },
          { id: "MAWeighted@tv-basicstudies", inputs: { length: 200 } },
          { id: "MASimple@tv-basicstudies", inputs: { length: 200 } },
        ],
        studies_overrides: {
          "moving average exponential.ma.color": "#c084fc",
          "moving average exponential.ma.linewidth": 2,
          "moving average weighted.ma.color": "#ff4757",
          "moving average weighted.ma.linewidth": 2,
          "moving average.ma.color": "#22d3ee",
          "moving average.ma.linewidth": 2,
        },
      });
      widgetRef.current = widget;
      widget.onChartReady?.(() => {
        if (cancelled) return;
        chartReadyRef.current = true;
        const chart = widget.activeChart?.();
        // Probe whether `createShape` exists on this tv.js build. If
        // undefined we fall back to the corner-overlay chips. Cannot
        // call here safely without a real position, so just record
        // capability detection.
        setChartApiSupported(typeof chart?.createShape === "function");
      });
    };

    if (window.TradingView) {
      mount();
    } else {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src="https://s3.tradingview.com/tv.js"]',
      );
      const script = existing ?? document.createElement("script");
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.onload = mount;
      if (!existing) document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      widgetRef.current?.remove?.();
      widgetRef.current = null;
      chartReadyRef.current = false;
      shapeIdsRef.current = new Map();
    };
  }, [containerId, tvSymbol]);

  // Draw / refresh entry-price markers whenever the matched positions
  // change. Wrapped in try/catch — the embedded tv.js doesn't always
  // expose createShape; a throw here would otherwise blank the chart.
  useEffect(() => {
    if (chartApiSupported !== true) return;
    const chart = widgetRef.current?.activeChart?.();
    if (!chart || typeof chart.createShape !== "function") return;

    const liveTickets = new Set(symbolMatchedPositions.map((p) => p.ticket));
    // Remove stale shapes for tickets that closed since last render.
    for (const [ticket, ids] of shapeIdsRef.current.entries()) {
      if (liveTickets.has(ticket)) continue;
      for (const id of ids) {
        try {
          chart.removeEntity?.(id);
        } catch {
          /* swallow — orphan id */
        }
      }
      shapeIdsRef.current.delete(ticket);
    }
    // Add shapes for any live tickets we haven't drawn yet.
    for (const pos of symbolMatchedPositions) {
      if (shapeIdsRef.current.has(pos.ticket)) continue;
      const colorHex = pos.side === "buy" ? "#10b981" : "#f43f5e";
      const ids: Array<string | number> = [];
      try {
        // Horizontal price line at entry — survives time-axis pans
        // and is the most reliable "where did I open" marker.
        const hlineId = chart.createShape(
          { price: pos.entry },
          {
            shape: "horizontal_line",
            lock: true,
            disableSelection: true,
            disableSave: true,
            disableUndo: true,
            text: `${pos.side === "buy" ? "BUY" : "SELL"} #${pos.ticket} · ${pos.lots.toFixed(2)} lots @ ${pos.entry}`,
            overrides: {
              linecolor: colorHex,
              linestyle: 2,
              linewidth: 1,
              showLabel: true,
              textcolor: colorHex,
              fontsize: 10,
              horzLabelsAlign: "right",
            },
          },
        );
        if (hlineId != null) ids.push(hlineId);
      } catch (e) {
        console.warn("[tv-chart] createShape(horizontal_line) failed", e);
      }
      try {
        // Arrow at the latest tick time as a side hint. Approximate;
        // real openedAt isn't persisted yet.
        const arrowId = chart.createShape(
          { time: Math.floor(pos.updatedAt / 1000), price: pos.entry },
          {
            shape: pos.side === "buy" ? "arrow_up" : "arrow_down",
            lock: true,
            disableSelection: true,
            disableSave: true,
            disableUndo: true,
            text: `#${pos.ticket}`,
            overrides: {
              arrowColor: colorHex,
              textcolor: colorHex,
              fontsize: 10,
            },
          },
        );
        if (arrowId != null) ids.push(arrowId);
      } catch (e) {
        console.warn("[tv-chart] createShape(arrow) failed", e);
      }
      if (ids.length > 0) shapeIdsRef.current.set(pos.ticket, ids);
    }
  }, [chartApiSupported, symbolMatchedPositions]);

  // Position markers live INSIDE the chart as horizontal price lines +
  // entry arrows (createShape, above). The corner overlay pills used
  // to mirror them as a fallback, but operators read pills as buttons
  // — they expected to click and act. Removed to make the chart the
  // single source of truth: the in-chart line is the marker and the
  // OrderPanel is the only place that sends commands.
  const openCount = symbolMatchedPositions.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24 }}
      className="flex h-full min-h-[420px] flex-col overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-foreground">
            TradingView{" "}
            <span className="text-accent">
              {market === "futures" ? "Futures Chart" : "FX & CFD Chart"}
            </span>
          </p>
          <p className="font-mono text-[10px] text-muted-foreground">
            {tvSymbol} ·{" "}
            <span style={{ color: "#c084fc" }}>EMA 100</span> ·{" "}
            <span style={{ color: "#ff4757" }}>WMA 200</span> ·{" "}
            <span style={{ color: "#22d3ee" }}>SMA 200</span>
            {openCount > 0 && ` · ${openCount} open`}
          </p>
        </div>
      </div>
      <div className="relative min-h-0 flex-1">
        <div id={containerId} className="absolute inset-0" />
      </div>
    </motion.div>
  );
}

function resolveTradingViewFxCfdSymbol(symbol: string): string {
  const clean = symbol.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const direct: Record<string, string> = {
    XAUUSD: "OANDA:XAUUSD",
    XAGUSD: "OANDA:XAGUSD",
    EURUSD: "OANDA:EURUSD",
    GBPUSD: "OANDA:GBPUSD",
    USDJPY: "OANDA:USDJPY",
    USDCHF: "OANDA:USDCHF",
    USDCAD: "OANDA:USDCAD",
    AUDUSD: "OANDA:AUDUSD",
    NZDUSD: "OANDA:NZDUSD",
    EURJPY: "OANDA:EURJPY",
    GBPJPY: "OANDA:GBPJPY",
    BTCUSD: "BITSTAMP:BTCUSD",
    ETHUSD: "BITSTAMP:ETHUSD",
    US30: "FPMARKETS:US30",
    NAS100: "FXPRO:US100",
    US100: "FXPRO:US100",
    SPX500: "FXPRO:US500",
    US500: "FXPRO:US500",
    GER40: "FXPRO:GER40",
    UK100: "FXPRO:UK100",
    USOIL: "TVC:USOIL",
    UKOIL: "TVC:UKOIL",
  };
  if (direct[clean]) return direct[clean];
  if (/^[A-Z]{6}$/.test(clean)) return `OANDA:${clean}`;
  return clean;
}
